import './connector.css'

import {
	IonButton,
	IonItem,
	IonList,
	IonSearchbar,
} from '@ionic/react'
import {
	IonSearchbarCustomEvent,
	SearchbarInputEventDetail,
} from '@ionic/core'
import {
	keepPreviousData,
	useQuery,
} from '@tanstack/react-query'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
	useMyRelevantTeam,
	useNewSeasonConnectorState,
} from './data'
import { useEnableSync } from './functions'
import {
	PlayHqOrganisationSearchResult,
	PlayHqSeasonOption,
	PlayHqTeamOption,
	fetchPlayHqFixtureGames,
	fetchPlayHqSeasonOptions,
	fetchPlayHqTeamOptions,
	isPlayHqNativeTransportAvailable,
	playHqTeamToConnector,
	searchPlayHqOrganisations,
} from './playHqApi'
import { formatDate } from './util'

type PlayHqSelectorProps = {
	selectionMode: 'draft' | 'current'
}

const CLUB_SEARCH_DEBOUNCE_MS = 400
const LOADING_MESSAGE_DELAY_MS = 1000
const PLAYHQ_SELECTOR_STALE_TIME_MS = 10 * 60 * 1000
const PLAYHQ_SELECTOR_GC_TIME_MS = 30 * 60 * 1000

type PlayHqTeamCardProps = {
	compName: string
	gradeName: string
	logoUrl?: string
	seasonName?: string
	teamName: string
	onClick?: (event: React.MouseEvent) => void
}

export const PlayHqTeamCard = (props: PlayHqTeamCardProps) => {
	const { compName, gradeName, logoUrl, seasonName, teamName, onClick } = props

	const content = (
		<>
			{
				logoUrl ? (
					<img src={logoUrl} className="playhq-team-picker" alt="" />
				) : null
			}
			<div className="nc-team-text">
				<div className="nc-team-name">
					{teamName}
				</div>
				<div className="nc-team-org">
					{compName}
				</div>
				<div className="nc-team-div">
					{gradeName}{seasonName ? `, ${seasonName}` : ''}
				</div>
			</div>
		</>
	)

	if (onClick) {
		return (
			<IonButton
				className="nc-team-button playhq-team-button"
				fill="outline"
				size="default"
				onClick={onClick}
			>
				{content}
			</IonButton>
		)
	}

	return (
		<div className="nc-team-card playhq-team-card">
			{content}
		</div>
	)
}

export const PlayHqSelector = (props: PlayHqSelectorProps) => {
	const { selectionMode } = props

	const relevantTeam = useMyRelevantTeam()
	const enableSync = useEnableSync()
	const [, setNewConnector] = useNewSeasonConnectorState()

	const [clubQuery, setClubQuery] = useState('')
	const [teamQuery, setTeamQuery] = useState('')
	const [selectedOrg, setSelectedOrg] = useState<PlayHqOrganisationSearchResult>()
	const [selectedSeason, setSelectedSeason] = useState<PlayHqSeasonOption>()
	const [selectedTeam, setSelectedTeam] = useState<PlayHqTeamOption>()

	const normalizedClubQuery = normalizeSearchQuery(clubQuery)
	const debouncedClubQuery = useDebouncedValue(normalizedClubQuery, CLUB_SEARCH_DEBOUNCE_MS)
	const clubSearchIsSettling = normalizedClubQuery !== debouncedClubQuery

	const clubSearchRef = useRef<HTMLIonSearchbarElement>(null)
	const teamSearchRef = useRef<HTMLIonSearchbarElement>(null)
	const canUsePlayHq = isPlayHqNativeTransportAvailable()

	const organisationsQuery = useQuery({
		queryKey: ['playhq-organisations', debouncedClubQuery],
		enabled: canUsePlayHq && debouncedClubQuery.length >= 2 && !selectedOrg,
		queryFn: () => searchPlayHqOrganisations(debouncedClubQuery),
		placeholderData: keepPreviousData,
		staleTime: PLAYHQ_SELECTOR_STALE_TIME_MS,
		gcTime: PLAYHQ_SELECTOR_GC_TIME_MS,
	})

	const seasonsQuery = useQuery({
		queryKey: ['playhq-seasons', selectedOrg?.routingCode],
		enabled: canUsePlayHq && !!selectedOrg && !selectedSeason,
		queryFn: () => selectedOrg ? fetchPlayHqSeasonOptions(selectedOrg) : Promise.resolve([]),
		staleTime: PLAYHQ_SELECTOR_STALE_TIME_MS,
		gcTime: PLAYHQ_SELECTOR_GC_TIME_MS,
	})

	const teamsQuery = useQuery({
		queryKey: ['playhq-teams', selectedSeason?.organisationCode, selectedSeason?.seasonId],
		enabled: canUsePlayHq && !!selectedSeason,
		queryFn: () => selectedSeason ? fetchPlayHqTeamOptions(selectedSeason) : Promise.resolve([]),
		staleTime: PLAYHQ_SELECTOR_STALE_TIME_MS,
		gcTime: PLAYHQ_SELECTOR_GC_TIME_MS,
	})

	const selectedConnector = selectedTeam ? playHqTeamToConnector(selectedTeam) : undefined
	const previewMatchesQuery = useQuery({
		queryKey: ['playhq-matches', selectedTeam?.gradeId, selectedTeam?.id],
		enabled: canUsePlayHq && !!selectedConnector,
		queryFn: () => selectedConnector ? fetchPlayHqFixtureGames(selectedConnector) : Promise.resolve([]),
		staleTime: PLAYHQ_SELECTOR_STALE_TIME_MS,
		gcTime: PLAYHQ_SELECTOR_GC_TIME_MS,
	})

	const isFetchingSelectorData = organisationsQuery.isFetching || seasonsQuery.isFetching || teamsQuery.isFetching
	const showLoadingMessage = useDelayedFlag(isFetchingSelectorData, LOADING_MESSAGE_DELAY_MS)

	const visibleTeams = useMemo(() => {
		const teams = teamsQuery.data ?? []
		if (!teams.length || selectedTeam) {
			return []
		}

		if (teamQuery.trim()) {
			return matchSorter(teams, teamQuery, { keys: ['teamName', 'gradeName', 'compName'] }).slice(0, 50)
		}

		if (relevantTeam?.name) {
			const matching = matchSorter(teams, relevantTeam.name, { keys: ['teamName'] })
			return (matching.length ? matching : teams).slice(0, 50)
		}

		return teams.slice(0, 50)
	}, [relevantTeam, selectedTeam, teamQuery, teamsQuery.data])

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout> | undefined
		if (!selectedOrg) {
			timer = setTimeout(() => {
				void clubSearchRef.current?.setFocus()
			}, 80)
		}
		return () => {
			if (timer) {
				clearTimeout(timer)
			}
		}
	}, [selectedOrg])

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout>
		if (selectedSeason && !selectedTeam) {
			timer = setTimeout(() => teamSearchRef.current?.setFocus(), 80)
		}
		return () => clearTimeout(timer)
	}, [selectedSeason, selectedTeam])

	const clearSelectedOrg = () => {
		setSelectedOrg(undefined)
		setSelectedSeason(undefined)
		setSelectedTeam(undefined)
		setTeamQuery('')
	}

	const clearSelectedSeason = () => {
		setSelectedSeason(undefined)
		setSelectedTeam(undefined)
		setTeamQuery('')
	}

	const handleUseSelectedTeam = async () => {
		if (!selectedTeam) return

		const connector = playHqTeamToConnector(selectedTeam)
		if (selectionMode === 'draft') {
			setNewConnector(connector)
			return
		}

		await enableSync(connector)
	}

	if (!canUsePlayHq) {
		return (
			<p className="connector-error">
				PlayHQ sync is currently available in the iOS and Android app.
			</p>
		)
	}

	const organisationResults = normalizedClubQuery.length >= 2 ? organisationsQuery.data ?? [] : []
	const seasonResults = seasonsQuery.data ?? []
	const previewGames = previewMatchesQuery.data ?? []
	const statusMessage =
		organisationsQuery.isError ? `Error: ${describeQueryError(organisationsQuery.error)}`
		: seasonsQuery.isError ? `Error: ${describeQueryError(seasonsQuery.error)}`
		: teamsQuery.isError ? `Error: ${describeQueryError(teamsQuery.error)}`
		: showLoadingMessage ? 'Loading...'
		: ''

	return (
		<div className="playhq-selector">
			<p className="playhq-selector-status" aria-live="polite">
				{statusMessage}
			</p>

			<IonList>
				{
					selectedOrg ? (
						<IonItem onClick={clearSelectedOrg}>
							<div>
								Club
							</div>
							<div slot="end">
								{selectedOrg.name}
							</div>
						</IonItem>
					) : (
						<>
							<PlayHqSearchBar
								placeholder="Club..."
								value={clubQuery}
								onText={setClubQuery}
								onEnter={() => {
									if (organisationResults.length) {
										setSelectedOrg(organisationResults[0])
									}
								}}
								ref={clubSearchRef}
							/>
							<IonList lines="none">
								{
									normalizedClubQuery.length >= 2 && !clubSearchIsSettling && !organisationsQuery.isFetching && !organisationResults.length ? (
										<IonItem>
											No clubs found.
										</IonItem>
									) : organisationResults.map(organisation => (
										<IonItem key={organisation.routingCode}>
											<IonButton
												fill="outline"
												size="default"
												onClick={event => {
													event.preventDefault()
													event.stopPropagation()
													setSelectedOrg(organisation)
												}}
											>
												{organisation.name}
											</IonButton>
										</IonItem>
									))
								}
							</IonList>
						</>
					)
				}

				{
					selectedOrg && !selectedSeason ? (
						<IonList lines="none">
							{
								!seasonsQuery.isFetching && !seasonResults.length ? (
									<IonItem>
										No seasons found.
									</IonItem>
								) : seasonResults.map(season => (
									<IonItem key={`${season.competitionId}-${season.seasonId}`}>
										<IonButton
											fill="outline"
											size="default"
											onClick={event => {
												event.preventDefault()
												event.stopPropagation()
												setSelectedSeason(season)
											}}
										>
											<span className={`playhq-season-option playhq-season-status-${season.status ? season.status.toLowerCase() : 'unknown'}`}>
												<span>{season.compName}</span>
												<small>{season.seasonName}{season.status ? ` (${season.status.toLowerCase()})` : ''}</small>
											</span>
										</IonButton>
									</IonItem>
								))
							}
						</IonList>
					) : null
				}

				{
					selectedSeason ? (
						<IonItem onClick={clearSelectedSeason}>
							<div>
								Season
							</div>
							<div slot="end">
								{selectedSeason.compName}, {selectedSeason.seasonName}
							</div>
						</IonItem>
					) : null
				}

				{
					selectedTeam ? (
						<IonItem onClick={() => setSelectedTeam(undefined)}>
							<div>
								Team
							</div>
							<div slot="end">
								<PlayHqTeamCard
									teamName={selectedTeam.teamName}
									compName={selectedTeam.compName}
									gradeName={selectedTeam.gradeName}
									logoUrl={selectedTeam.logoUrl}
									seasonName={selectedTeam.seasonName}
								/>
							</div>
						</IonItem>
					) : selectedSeason ? (
						<>
							<PlayHqSearchBar
								placeholder="Team..."
								value={teamQuery}
								onText={setTeamQuery}
								onEnter={() => {
									if (visibleTeams.length) {
										setSelectedTeam(visibleTeams[0])
									}
								}}
								ref={teamSearchRef}
							/>
							<IonList lines="none">
								{
									!teamsQuery.isFetching && !visibleTeams.length ? (
										<IonItem>
											No teams found.
										</IonItem>
									) : visibleTeams.map(team => (
										<IonItem key={`${team.gradeId}-${team.id}`}>
											<PlayHqTeamCard
												onClick={(event: React.MouseEvent) => {
													event.preventDefault()
													event.stopPropagation()
													setSelectedTeam(team)
												}}
												compName={team.compName}
												gradeName={team.gradeName}
												logoUrl={team.logoUrl}
												seasonName={team.seasonName}
												teamName={team.teamName}
											/>
										</IonItem>
									))
								}
							</IonList>
						</>
					) : null
				}

				{
					selectedTeam && previewGames.length ? (
						<div className="nc-matches-list">
							{
								previewGames.slice(0, 8).map(match => (
									<div
										className="nc-match"
										key={match.id}
									>
										<div className="field-round">
											<div>
												{match.round}
											</div>
										</div>
										<div className="field-opponent">
											{match.opponent}
										</div>
										<div className="field-date">
											{formatDate(match.date)}
										</div>
										&bull;
										<div className="field-venue">
											{match.venue}
										</div>
									</div>
								))
							}
						</div>
					) : null
				}

				{
					selectedTeam ? (
						<IonButton
							className="nc-sync-button"
							size="default"
							expand="block"
							slot="end"
							onClick={() => {
								void handleUseSelectedTeam()
							}}
						>
							{selectionMode === 'draft' ? 'Use This Team' : 'Enable Online Sync'}
						</IonButton>
					) : null
				}
			</IonList>
		</div>
	)
}

type PlayHqSearchBarProps = {
	placeholder: string
	value: string
	onText: (text: string) => void
	onEnter: () => void
}

const PlayHqSearchBar = React.forwardRef<HTMLIonSearchbarElement, PlayHqSearchBarProps>((props, ref) => {
	const { placeholder, value, onText, onEnter } = props

	return (
		<IonSearchbar
			ref={ref}
			placeholder={placeholder}
			value={value}
			onIonInput={(event: IonSearchbarCustomEvent<SearchbarInputEventDetail>) => {
				const searchbar = event.target as HTMLIonSearchbarElement | null
				onText(event.detail.value ?? searchbar?.value ?? '')
			}}
			onKeyDown={event => {
				if (event.code === 'Enter' || event.code === 'NumpadEnter') {
					onEnter()
				}
			}}
		/>
	)
})
PlayHqSearchBar.displayName = 'PlayHqSearchBar'

function useDebouncedValue<T>(value: T, delayMs: number) {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delayMs)
		return () => clearTimeout(timer)
	}, [delayMs, value])

	return debouncedValue
}

function useDelayedFlag(value: boolean, delayMs: number) {
	const [delayedValue, setDelayedValue] = useState(false)

	useEffect(() => {
		if (!value) {
			setDelayedValue(false)
			return
		}

		const timer = setTimeout(() => setDelayedValue(true), delayMs)
		return () => clearTimeout(timer)
	}, [delayMs, value])

	return delayedValue
}

function normalizeSearchQuery(query: string) {
	return query.trim().replace(/\s+/g, ' ')
}

function describeQueryError(error: unknown) {
	if (error instanceof Error) {
		return error.message
	}

	if (typeof error === 'string') {
		return error
	}

	return 'Unknown error'
}
